import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import { formatPhoneInput, isValidPhoneNumber } from '../../../utils/phoneFormatter';

const AuthForm = ({ 
  mode = 'login',
  onSuccess,
  onModeChange,
  className = ''
}) => {
  const authContext = useAuth();
  const { signIn, signUp, loading, error, clearError } = authContext;
  
  // Debug logging removed for performance
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    acceptTerms: false
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    // Format phone number input
    const formattedValue = field === 'phone' ? formatPhoneInput(value) : value;
    
    setFormData(prev => ({ ...prev, [field]: formattedValue }));
    
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Clear auth error when user starts typing
    if (error) {
      clearError();
    }
  };

  const validateForm = () => {
    const errors = {};

    // Email validation
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (mode === 'signup' && formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters long';
    }

    // Confirm password validation (signup only)
    if (mode === 'signup') {
      if (!formData.confirmPassword) {
        errors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }

      // Required personal information validation
      if (!formData.firstName.trim()) {
        errors.firstName = 'First name is required';
      }
      if (!formData.lastName.trim()) {
        errors.lastName = 'Last name is required';
      }

      if (!formData.phone.trim()) {
        errors.phone = 'Phone number is required';
      } else if (!isValidPhoneNumber(formData.phone)) {
        errors.phone = 'Please enter a valid phone number (e.g., (555) 123-4567)';
      }

      // Terms acceptance validation
      if (!formData.acceptTerms) {
        errors.acceptTerms = 'You must accept the terms and conditions';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Check if signIn function is available
    if (!signIn || typeof signIn !== 'function') {
      console.error('signIn function is not available:', signIn);
      setValidationErrors({ general: 'Authentication service is not available. Please refresh the page.' });
      return;
    }

    setIsSubmitting(true);

    try {
      if (mode === 'signin') {
        console.log('Attempting sign in...');
        const result = await signIn(formData.email, formData.password);
        console.log('Sign in result:', result);
        
        if (result.error) {
          console.error('Sign in failed:', result.error);
          // Error is already set in AuthContext
          return;
        }

        console.log('Sign in successful, calling onSuccess');
        // Success - user is now authenticated
        onSuccess?.(result.data.user);
      } else {
        // Check if signUp function is available
        if (!signUp || typeof signUp !== 'function') {
          console.error('signUp function is not available:', signUp);
          setValidationErrors({ general: 'Authentication service is not available. Please refresh the page.' });
          return;
        }
        
        const additionalData = {
          first_name: formData.firstName,
          last_name: formData.lastName,
          full_name: `${formData.firstName} ${formData.lastName}`.trim(),
          phone: formData.phone
        };

        const result = await signUp(formData.email, formData.password, additionalData);
        
        if (result.error) {
          // Error is already set in AuthContext
          return;
        }

        // Success - show appropriate message based on confirmation status
        if (result.data?.user?.email_confirmed_at) {
          // Auto-confirmed (no email confirmation needed)
          alert('Account created successfully! You are now logged in.');
          onSuccess?.(result.data.user);
        } else {
          // Email confirmation required
          alert('Account created successfully! Please check your email to confirm your account before signing in.');
          onModeChange('signin');
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDisabled = loading || isSubmitting;
  
  // Force reset loading state if it's been stuck for too long
  useEffect(() => {
    if (loading) {
      const timeout = setTimeout(() => {
        console.log('Loading state timeout - forcing reset');
        // Force a page reload to reset everything
        window.location.reload();
      }, 15000); // 15 second timeout
      
      return () => clearTimeout(timeout);
    }
  }, [loading]);
  
  // Debug logging
  console.log('AuthForm Debug:', {
    loading,
    isSubmitting,
    isDisabled,
    error
  });

  return (
    <div className={`w-full max-w-md mx-auto ${className}`}>
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-2">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {mode === 'signin' ? 'Sign In' : 'Create Account'}
          </h2>
          <p className="text-gray-600">
            {mode === 'signin' 
              ? 'Welcome back! Please sign in to continue.' 
              : <>Join Career<strong>Lift</strong> to advance your career.</>
            }
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <Icon name="AlertCircle" size={20} color="#dc2626" className="mr-3 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-red-800">Authentication Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {validationErrors.general && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <Icon name="AlertCircle" size={20} color="#dc2626" className="mr-3 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-red-800">Service Error</h3>
                <p className="text-sm text-red-700 mt-1">{validationErrors.general}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information (Signup only) */}
          {mode === 'signup' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="First Name"
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  error={validationErrors.firstName}
                  placeholder="Enter your first name"
                  disabled={isDisabled}
                  required
                />
                <Input
                  label="Last Name"
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  error={validationErrors.lastName}
                  placeholder="Enter your last name"
                  disabled={isDisabled}
                  required
                />
              </div>

              <Input
                label="Phone Number"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                error={validationErrors.phone}
                placeholder="(555) 123-4567"
                disabled={isDisabled}
                required
              />
            </>
          )}

          {/* Email */}
          <Input
            label="Email Address"
            type="email"
            value={formData.email}
            onChange={(e) => {
              console.log('Email input changed:', e.target.value);
              handleInputChange('email', e.target.value);
            }}
            onFocus={() => console.log('Email input focused')}
            onBlur={() => console.log('Email input blurred')}
            onClick={() => console.log('Email input clicked')}
            error={validationErrors.email}
            placeholder="Enter your email address"
            disabled={isDisabled}
            required
            autoComplete="email"
            style={{ pointerEvents: 'auto' }}
          />

          {/* Password */}
          <Input
            label="Password"
            type="password"
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            error={validationErrors.password}
            placeholder="Enter your password"
            disabled={isDisabled}
            required
          />

          {/* Confirm Password (Signup only) */}
          {mode === 'signup' && (
            <Input
              label="Confirm Password"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              error={validationErrors.confirmPassword}
              placeholder="Confirm your password"
              disabled={isDisabled}
              required
            />
          )}

          {/* Terms Acceptance (Signup only) */}
          {mode === 'signup' && (
            <div className="flex items-start">
              <Checkbox
                checked={formData.acceptTerms}
                onChange={(checked) => handleInputChange('acceptTerms', checked)}
                disabled={isDisabled}
              />
              <label className="ml-3 text-sm text-gray-700">
                I agree to the{' '}
                <a href="/terms-of-service" className="text-blue-600 hover:text-blue-500 underline">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="/privacy-policy" className="text-blue-600 hover:text-blue-500 underline">
                  Privacy Policy
                </a>
              </label>
            </div>
          )}

          {validationErrors.acceptTerms && (
            <p className="text-red-600 text-sm mt-1">{validationErrors.acceptTerms}</p>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full"
            disabled={isDisabled}
            loading={isDisabled}
          >
            {mode === 'signin' ? 'Sign In' : 'Create Account'}
          </Button>
        </form>

        {/* Mode Toggle */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            {mode === 'signin' ? "Don't have an account? " : "Already have an account? "}
            <button
              type="button"
              onClick={() => onModeChange(mode === 'signin' ? 'signup' : 'signin')}
              className="text-blue-600 hover:text-blue-500 font-medium underline"
              disabled={isDisabled}
            >
              {mode === 'signin' ? 'Sign up' : 'Sign in'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthForm;